
import { Component } from '@angular/core';

import { PeriodDto } from '@models/users/period.dto';
import { ContactService } from '@services/contact.service';

type AvailabilityUnit = PeriodDto['units'][number];

interface AvailabilityWeek {
  startDate: Date;
  endDate: Date;
  units: AvailabilityUnit[];
}

@Component({
  selector: 'ebs-availability-dialog',
  templateUrl: './availability-dialog.component.html',
  styleUrls: ['./availability-dialog.component.scss'],
})
export class AvailabilityDialogComponent {

  /* ==============================================
     DATI
     ============================================== */

  public periods: AvailabilityWeek[] = [];

  public activeIndex = 0;

  /* ==============================================
     GIORNATA CORRENTE
     ============================================== */

  public get activeDay(): number {
    const period = this.periods[this.activeIndex];

    if (!period) {
      return -1;
    }

    return period.units.findIndex(
      (unit) =>
        this.isToday(unit.unitDate) &&
        unit.isActive
    );
  }

  /* ==============================================
     COSTRUTTORE
     ============================================== */

  constructor(
    private contactService: ContactService,
  ) {
    this.loadPeriods();
  }

  /* ==============================================
     CARICAMENTO REPERIBILITÀ
     ============================================== */

  public loadPeriods(): void {
    this.contactService
      .getAvailabilityView()
      .subscribe({
        next: (periods: PeriodDto[]) => {
          this.periods = this.buildWeeks(periods);

          const currentWeekIndex =
            this.periods.findIndex((week) => {
              const today = this.startOfDay(new Date());

              return (
                today >= week.startDate &&
                today <= week.endDate
              );
            });

          this.activeIndex =
            currentWeekIndex >= 0
              ? currentWeekIndex
              : 0;
        },

        error: (error) => {
          console.error(
            'Errore caricamento reperibilità:',
            error
          );

          this.periods = [];
          this.activeIndex = 0;
        },
      });
  }

  /* ==============================================
     COSTRUZIONE SETTIMANE LUNEDÌ-DOMENICA
     ============================================== */

  private buildWeeks(
    sourcePeriods: PeriodDto[],
  ): AvailabilityWeek[] {

    const currentMonday = this.getMonday(new Date());

    const unitsByDate = new Map<
      string,
      AvailabilityUnit
    >();

    // Raccoglie le giornate ricevute dal servizio,
    // senza modificare gli oggetti originali.

    sourcePeriods.forEach((period) => {
      period.units.forEach((unit) => {
        const date = this.toLocalDate(unit.unitDate);

        if (Number.isNaN(date.getTime())) {
          return;
        }

        const key = this.dateKey(date);

        unitsByDate.set(key, {
          ...unit,
        });
      });
    });

    if (unitsByDate.size === 0) {
      return [];
    }

    // Include tutte le date restituite dal backend, anche in altri mesi.
    // La settimana corrente deve essere sempre navigabile quando esiste
    // almeno una programmazione.
    const dates = Array.from(unitsByDate.keys())
      .map((key) => this.parseDateKey(key))
      .sort((a, b) => a.getTime() - b.getTime());

    const firstMonday = this.getMonday(
      dates[0] < currentMonday ? dates[0] : currentMonday
    );
    const lastDate = dates[dates.length - 1];
    const lastSunday = this.getSunday(
      lastDate > currentMonday ? lastDate : currentMonday
    );

    const weeks: AvailabilityWeek[] = [];

    let weekStart = new Date(firstMonday);

    while (weekStart <= lastSunday) {

      const weekEnd =
        this.getSunday(weekStart);

      const weekUnits: AvailabilityUnit[] = [];

      for (let day = 0; day < 7; day++) {

        const currentDate =
          this.addDays(weekStart, day);

        const key =
          this.dateKey(currentDate);

        const existingUnit =
          unitsByDate.get(key);

        if (existingUnit) {

          weekUnits.push({
            ...existingUnit,
          });

        } else {

          // Giornata non presente nel backend:
          // viene mostrata come non attiva,
          // senza attribuire turni inesistenti.

          weekUnits.push({
            unitDate: new Date(currentDate),
            isActive: false,
            times: [],
          } as AvailabilityUnit);

        }
      }

      weeks.push({
        startDate: new Date(weekStart),
        endDate: new Date(weekEnd),
        units: weekUnits,
      });

      weekStart =
        this.addDays(weekStart, 7);
    }

    return weeks;
  }

  /* ==============================================
     VERIFICA GIORNATA ODIERNA
     ============================================== */

  public isToday(
    value: string | Date,
  ): boolean {

    const date = this.startOfDay(
      this.toLocalDate(value)
    );

    const today = this.startOfDay(
      new Date()
    );

    return (
      date.getTime() === today.getTime()
    );
  }

  /* ==============================================
     VERIFICA GIORNATA DISABILITATA
     ============================================== */

  public isPastDay(value: string | Date): boolean {
    const date = this.startOfDay(this.toLocalDate(value));
    const today = this.startOfDay(new Date());
    return date.getTime() < today.getTime();
  }

  public isDayDisabled(
    unit: AvailabilityUnit,
    _index: number,
  ): boolean {
    // I giorni trascorsi non si espandono, anche se avevano turni.
    // I giorni senza servizio non si espandono mai.
    return this.isPastDay(unit.unitDate) || !unit.isActive;
  }

  /* ==============================================
     NAVIGAZIONE
     ============================================== */

  public previousPeriod(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  public nextPeriod(): void {
    if (
      this.activeIndex <
      this.periods.length - 1
    ) {
      this.activeIndex++;
    }
  }

  /* ==============================================
     ETICHETTA SETTIMANA
     ============================================== */

  public getWeekLabel(
    index: number,
  ): string {

    const week = this.periods[index];

    if (!week) {
      return '';
    }

    const start = this.formatShortDate(
      week.startDate
    );

    const end = this.formatShortDate(
      week.endDate
    );

    return `${start} - ${end}`;
  }

  /* ==============================================
     UTILITY DATE
     ============================================== */

  private toLocalDate(value: string | Date): Date {
    if (value instanceof Date) {
      return new Date(value);
    }

    // Evita lo slittamento di un giorno per date ISO senza orario.
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    return new Date(value);
  }

  private startOfDay(
    date: Date,
  ): Date {

    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  }

  private getMonday(
    date: Date,
  ): Date {

    const result =
      this.startOfDay(date);

    const day =
      result.getDay();

    const difference =
      day === 0
        ? -6
        : 1 - day;

    result.setDate(
      result.getDate() + difference
    );

    return result;
  }

  private getSunday(
    date: Date,
  ): Date {

    const monday =
      this.getMonday(date);

    return this.addDays(
      monday,
      6
    );
  }

  private addDays(
    date: Date,
    days: number,
  ): Date {

    const result =
      new Date(date);

    result.setDate(
      result.getDate() + days
    );

    return result;
  }

  private dateKey(
    date: Date,
  ): string {

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, '0');

    const day =
      String(
        date.getDate()
      ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseDateKey(
    value: string,
  ): Date {

    const [
      year,
      month,
      day
    ] = value
      .split('-')
      .map(Number);

    return new Date(
      year,
      month - 1,
      day
    );
  }

  private formatShortDate(
    date: Date,
  ): string {

    return new Intl.DateTimeFormat(
      'it-IT',
      {
        day: '2-digit',
        month: 'short',
      }
    ).format(date);
  }
}
